/**
 * MfaEnrollment — the ONE screen a sign-in that may not proceed without a second factor enrols through.
 *
 * Two sign-ins refuse a session (403 `MFA_SETUP_REQUIRED`) with a ten-minute enrolment token: every platform API for a
 * staff member with no second factor (Q-L16-98 · F-98-16), and every tenant sign-in for a person whose MFA an
 * administrator's reset cleared (owner ruling Q-ARG-1, "Required again", 2026-10-08). Before this screen no client read
 * that refusal, so such an account could never sign in at all. `@puffin-core/auth-context` (both modes) turns the
 * refusal into `auth.mfaEnrollment` and the begin / complete / finish / cancel actions; `@puffin-core/login-shell`'s
 * `<LoginForm>` renders this screen from them when `auth.mfaEnrollmentRequired` is true. It was `MfaEnrollment`
 * until the tenant sign-in began to render it.
 *
 * Steps (derived from the enrolment state, never duplicated):
 *   1. password — re-prove the password (the server's one contract for `/mfa/setup/init`);
 *   2. configure → verify — `<MfaSetupWizard>` over the secret, then the first code;
 *   3. backup — the backup codes; "Complete Setup" publishes the session (`onFinish`), which is
 *      why the context holds the user until now — or, when the company policy still owes ANOTHER factor
 *      (SECREQ S5: the completion carried no session), raises that factor's hold instead.
 * And one more state: `enrollment.alreadyEnrolled`, raised when a step-3 retry finds MFA already
 * enabled because the earlier success response was lost. The screen says so, tells the staff member how
 * to get new backup codes once signed in, and moves focus to "Sign in" (the cancel action, which returns
 * to the login form). It never shows the generic "MFA setup failed".
 *
 * View-layer only: the actions are the context's, injected as props, so this package does not
 * depend on `@puffin-core/auth-context`.
 *
 * @module @puffin-core/mfa-ui/MfaEnrollment
 */

import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { PasswordField } from '@puffin-core/auth-shell';

import { MfaSetupWizard } from './MfaSetupWizard.jsx';
import { useMfaTranslate } from './hooks/useMfaTranslate.js';
import { mfaRefusalText } from './hooks/useMfaRefusalText.js';

/**
 * Why the person is enrolling, which is the one sentence that differs (N152): a sign-in held until a second factor
 * exists (`'sign-in'`, the login shell), or a signed-in person turning it on from their security settings
 * (`'settings'`, `<MfaSettingsCard>`). Every step after the password is the same screen.
 */
export const MFA_ENROLLMENT_PURPOSES = Object.freeze(['sign-in', 'settings']);

const INTRO_KEY_BY_PURPOSE = Object.freeze({
  'sign-in': 'enroll_intro',
  settings: 'enroll_intro_settings',
});

/**
 * The sentence a refused enrolment step shows: catalog words chosen by the refusal's CODE, its HTTP STATUS or its
 * CLASS — never the server's English sentence and never a thrown Error's message (N152 / N138,
 * `web/no-thrown-message-as-copy`).
 *
 * - 403 `CREDENTIAL_INCORRECT`: the wrong password or code (N139, `mfaRefusalText`).
 * - 503 `AUDIT_RECORD_UNAVAILABLE`: the step's record could not be kept, so nothing changed (Q-EE-22,
 *   `mfaRefusalText`).
 * - 429: the setup routes' limiter refused another attempt.
 * - 401: the sign-in's ten-minute enrolment token ran out (an in-session enrolment never rejects: `useMfaSettings`
 *   answers its own failures).
 * - anything else — a lost connection among them (auth-client's network failure carries no status): the setup failed,
 *   try again.
 *
 * @param {*} err - What the step rejected with
 * @param {'password'|'code'} kind - The secret the step asked for
 * @param {(key: string) => string} str - The `mfa` corpus translator
 * @returns {string}
 */
function enrollmentRefusalText(err, kind, str) {
  const credential = mfaRefusalText(err, kind, str);
  if (credential) return credential;
  if (err?.status === 429) return str('error_rate_limited');
  if (err?.status === 401) return str('error_enrollment_expired');
  return str('error_setup_failed');
}

/** Step 1 — confirm the password. Module-private. */
function EnrollmentPasswordStep({ appName, purpose, loading, error, onBegin, onCancel, t }) {
  const str = useMfaTranslate(t);
  const [password, setPassword] = useState('');

  const handleSubmit = (event) => {
    event.preventDefault();
    if (password.length > 0 && !loading) {
      // `onBegin` is `attempt`-wrapped: a refusal is worded and resolves `null`; the form stays for
      // another attempt.
      onBegin(password);
    }
  };

  return (
    <form onSubmit={handleSubmit} data-testid="mfa-enrollment-password">
      <h3 className="mfa-challenge-title">{str('enroll_title')}</h3>
      <p className="mfa-challenge-subtitle">{str(INTRO_KEY_BY_PURPOSE[purpose], { appName })}</p>
      {error && (
        <div className="form-error" role="alert">
          {error}
        </div>
      )}
      {/* auth-shell's ONE `<PasswordField>` (PALM-PHONE-UAT-001 A6, owner ruling R-5): the password
          proof before an enrolment can be checked while it is typed. Required on the control, not
          the label, as every field of this flow is. */}
      <PasswordField
        id="mfa-enrollment-password"
        label={str('enroll_password_label')}
        value={password}
        onChange={(event) => setPassword(event.target.value)}
        autoComplete="current-password"
        disabled={loading}
        inputProps={{ 'aria-required': 'true' }}
      />
      <div className="mfa-setup-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={loading}>
          {str('enroll_btn_cancel')}
        </button>
        <button type="submit" className="btn btn-primary" disabled={loading || !password}>
          {str('enroll_btn_continue')}
        </button>
      </div>
    </form>
  );
}

EnrollmentPasswordStep.propTypes = {
  appName: PropTypes.string.isRequired,
  purpose: PropTypes.oneOf(MFA_ENROLLMENT_PURPOSES).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onBegin: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  t: PropTypes.func,
};

/**
 * The lost-success retry's state: MFA is already set up, so the staff member signs in (the screen's
 * cancel action returns to the login form). The message is announced (`role="status"`) and focus moves
 * to the one action. Module-private.
 */
function EnrollmentAlreadyEnabledStep({ onSignIn, t }) {
  const str = useMfaTranslate(t);
  const signInRef = useRef(null);
  useEffect(() => {
    signInRef.current.focus();
  }, []);

  return (
    <div data-testid="mfa-enrollment-already-enabled">
      {/* h2: the step renders inside the login shell's card, under its h1 (as MfaVerification does). */}
      <h2 className="mfa-challenge-title">{str('enroll_already_title')}</h2>
      <p className="mfa-challenge-subtitle" role="status">
        {str('enroll_already_body')}
      </p>
      <p className="mfa-challenge-subtitle">{str('enroll_already_backup_codes')}</p>
      <div className="mfa-setup-actions">
        <button ref={signInRef} type="button" className="btn btn-primary" onClick={onSignIn}>
          {str('enroll_already_btn_sign_in')}
        </button>
      </div>
    </div>
  );
}

EnrollmentAlreadyEnabledStep.propTypes = {
  onSignIn: PropTypes.func.isRequired,
  t: PropTypes.func,
};

/**
 * @param {Object} props
 * @param {string} props.appName - the console's brand, shown in the copy.
 * @param {'sign-in'|'settings'} [props.purpose='sign-in'] - why the person enrols (`MFA_ENROLLMENT_PURPOSES`): the
 *   password step's introduction follows it.
 * @param {{setup: ?{secret: string, qrUri: string}, backupCodes: ?string[], alreadyEnrolled: boolean}}
 *   props.enrollment - `auth.mfaEnrollment`.
 * @param {boolean} props.loading - `auth.isLoading`.
 * @param {?string} [props.error] - `auth.error`.
 * @param {(password: string) => Promise} props.onBegin - `auth.beginMfaEnrollment`; it REJECTS a refused step (the
 *   context records no sentence), and the screen words the refusal from the catalog and resolves `null`.
 * @param {(code: string) => Promise} props.onComplete - `auth.completeMfaEnrollment`; same contract.
 * @param {() => void} props.onFinish - `auth.finishMfaEnrollment`.
 * @param {() => void} props.onCancel - `auth.cancelMfaEnrollment`.
 * @param {Function} [props.t] - an i18next translator (see `useMfaTranslate`).
 * @returns {React.JSX.Element}
 */
export function MfaEnrollment({
  appName,
  purpose = 'sign-in',
  enrollment,
  loading,
  error = null,
  onBegin,
  onComplete,
  onFinish,
  onCancel,
  t,
}) {
  const str = useMfaTranslate(t);
  const [codeRequested, setCodeRequested] = useState(false);
  // Every refused step is worded HERE, from the catalog (N139 / N152 / N138): the context records no sentence for it,
  // and the failure's English message is never shown. Cleared on each attempt.
  //
  // REPORT, never report AND throw (SWALLOWED-CATCH-BURNDOWN-001 U3): the context rethrows a failed step, this words
  // it and the attempt resolves `null`. Both callers fire it from an event handler without awaiting, so a rethrow here
  // would be an unhandled rejection on every wrong password.
  const [refusal, setRefusal] = useState(null);
  const attempt = (action, kind) => (value) => {
    setRefusal(null);
    return action(value).catch((err) => {
      setRefusal(enrollmentRefusalText(err, kind, str));
      return null;
    });
  };
  const shownError = refusal || error;

  if (enrollment.alreadyEnrolled) {
    return <EnrollmentAlreadyEnabledStep onSignIn={onCancel} t={t} />;
  }

  if (!enrollment.setup) {
    return (
      <EnrollmentPasswordStep
        appName={appName}
        purpose={purpose}
        loading={loading}
        error={shownError}
        onBegin={attempt(onBegin, 'password')}
        onCancel={onCancel}
        t={t}
      />
    );
  }

  let step = 'configure';
  if (enrollment.backupCodes) step = 'backup';
  else if (codeRequested) step = 'verify';

  return (
    <MfaSetupWizard
      t={t}
      step={step}
      loading={loading}
      error={shownError}
      secret={enrollment.setup.secret}
      qrUri={enrollment.setup.qrUri}
      backupCodes={enrollment.backupCodes || undefined}
      appName={appName}
      onVerify={(code) => {
        if (step === 'configure') {
          setCodeRequested(true);
          return;
        }
        // A refused code stays on the verify step, worded by `attempt` (which resolves `null`).
        attempt(onComplete, 'code')(code);
      }}
      onComplete={onFinish}
      onCancel={onCancel}
    />
  );
}

MfaEnrollment.propTypes = {
  appName: PropTypes.string.isRequired,
  purpose: PropTypes.oneOf(MFA_ENROLLMENT_PURPOSES),
  enrollment: PropTypes.shape({
    setup: PropTypes.shape({ secret: PropTypes.string, qrUri: PropTypes.string }),
    backupCodes: PropTypes.arrayOf(PropTypes.string),
    alreadyEnrolled: PropTypes.bool,
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  error: PropTypes.string,
  onBegin: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  onFinish: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  t: PropTypes.func,
};

export default MfaEnrollment;
